import { Button, Divider, Dropdown, Input, MenuProps, message, Space, Spin, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-layout";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { getDataTools, updateBrandDetect, updateBroadcastMsg } from "services";
import { updateToolVersion } from "services/version";
import { convertDateTime } from "utils";
import { logOutAllUsers, logOutAllUsersByType } from "../../services/user";

const Tools = () => {
  const [dataTools, setDataTools] = useState<any>({
    broadcastMsg: "",
    chromeVersion: "",
    brandDetectMsg: "",
    brandDetectMsgLatest: "",
    amazonBSRLatest: "",
    saleEstDataLatest: []
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingField, setLoadingField] = useState<boolean>(false);
  const [fieldUpdate, setFieldUpdate] = useState<string>("");

  const loadData = () => {
    setLoading(true);
    getDataTools()
      .then(({ data: { data: res } }: any) =>
        setDataTools(() => {
          setLoading(false);
          return ({
            broadcastMsg: res.bmessage.bMessage,
            chromeVersion: res.version.versionChrome,
            brandDetectMsg: res.brand_bmessage.bMessage,
            brandDetectMsgLatest: res.brand_bmessage.date,
            amazonBSRLatest: convertDateTime(res.amazonBsr_lastest.lastUpdate),
            saleEstDataLatest: res.est_sale_last_update,
          })
        })
      )
  };

  const handleOnLogAllOut: MenuProps['onClick'] = e => {
    setFieldUpdate(() => {
      setLoadingField(true);
      return "logoutAll";
    });
    let onLogout = logOutAllUsers()
    if (e.key !== "ALL") {
      const request = {
        type: e.key
      }
      onLogout = logOutAllUsersByType(request)
    }
    onLogout
      .then(() => message.success("Logged all of users out successfully"))
      .catch(() => message.error("Can not log all of users out"))
      .finally(() => setLoadingField(false))
  };

  const handleOnUpdateBroadcastMsg = (value: string, fieldName: string) => {
    setFieldUpdate(() => {
      setLoadingField(true);
      return fieldName;
    })
    const params = {
      broadcastMsg: value
    }
    updateBroadcastMsg(params)
      .then(() => {
        message.success("Updated Broadcast Message successfully");
        setDataTools((prev: any) => ({ ...prev, broadcastMsg: value }))
      })
      .catch(() => {
        message.error("Can not update Broadcast Message")
      })
      .finally(() => setLoadingField(false))
  };

  const handleOnUpdateChromeVersion = (value: string, fieldName: string) => {
    setFieldUpdate(() => {
      setLoadingField(true);
      return fieldName;
    })
    const params = {
      chromeVersion: value
    }
    updateToolVersion(params)
      .then(() => {
        message.success("Updated Chrome version successfully");
        setDataTools((prev: any) => ({ ...prev, broadcastMsg: value }))
      })
      .catch(() => {
        message.error("Can not update Chrome version")
      })
      .finally(() => setLoadingField(false))
  };

  const handleOnUpdateBrandDetect = (value: string, type: "msg" | "date", fieldName: string) => {
    setFieldUpdate(() => {
      setLoadingField(true);
      return fieldName;
    })
    const params = {
      brandDetectMsg: type === "msg" && value,
      date: type === "date" && value
    }

    updateBrandDetect(params)
      .then(() => {
        message.success("Updated Brand detect successfully");
        setDataTools((prev: any) => {
          const result = type === "msg" ? { brandDetectMsg: value } : { brandDetectMsgLatest: value }
          return ({ ...prev, ...result })
        })
      })
      .catch(() => {
        message.error("Can not update Brand detect")
      })
      .finally(() => setLoadingField(false))
  };

  const menuLogout: MenuProps = ({
    onClick: handleOnLogAllOut,
    items: [
      {
        label: 'Logout ADDON',
        key: 'ADDON',
      },
      {
        label: 'Logout AZINSIGHT',
        key: 'AZINSIGHT',
      },
      {
        label: 'Logout AZMOBILE',
        key: 'AZMOBILE',
      },
      {
        label: 'Logout ALL',
        key: 'ALL',
      },
    ]
  });

  useEffect(() => {
    loadData()
  }, []);

  return (
    <>
      <Helmet>
        <title>Tools | Admin UI</title>
      </Helmet>
      <PageHeader
        className="page-header"
        title="Tools"
        extra={
          <>
            <Button type="primary" onClick={() => loadData()}>Refresh</Button>
            <Dropdown menu={menuLogout} disabled={fieldUpdate === "logoutAll" && loadingField}>
              <Button danger>
                <Space>
                  Logout
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </>
        }
        footer={
          loading ? (
            <div className="loading-page"><Spin size="large" /></div>
          ) : (
            <Space direction="vertical" style={{ width: "100%", padding: 20 }}>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>AZInsight Broadcast Message</Typography.Title>
                </Divider>
                <Input.Group compact>
                  <Input.TextArea
                    value={dataTools.broadcastMsg}
                    disabled={fieldUpdate === "broadcastMsg" && loadingField}
                    onChange={(e) => setDataTools((prev: any) => ({ ...prev, broadcastMsg: e.target.value }))}
                    style={{ width: 'calc(100% - 100px)' }}
                  />
                  <Button
                    type="primary"
                    loading={fieldUpdate === "broadcastMsg" && loadingField}
                    onClick={() => handleOnUpdateBroadcastMsg(dataTools.broadcastMsg, "broadcastMsg")}
                  >
                    Save
                  </Button>
                </Input.Group>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>Chrome version</Typography.Title>
                </Divider>
                <Input.Group compact>
                  <Input
                    value={dataTools.chromeVersion}
                    disabled={fieldUpdate === "chromeVersion" && loadingField}
                    onChange={(e) => setDataTools((prev: any) => ({ ...prev, chromeVersion: e.target.value }))}
                    style={{ width: 'calc(100% - 100px)' }}
                  />
                  <Button
                    type="primary"
                    loading={fieldUpdate === "chromeVersion" && loadingField}
                    onClick={() => handleOnUpdateChromeVersion(dataTools.chromeVersion, "chromeVersion")}
                  >
                    Save
                  </Button>
                </Input.Group>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>Brand Detect Message</Typography.Title>
                </Divider>
                <Input.Group compact>
                  <Input
                    value={dataTools.brandDetectMsg}
                    disabled={fieldUpdate === "brandDetectMsg" && loadingField}
                    onChange={(e) => setDataTools((prev: any) => ({ ...prev, brandDetectMsg: e.target.value }))}
                    style={{ width: 'calc(100% - 100px)' }}
                  />
                  <Button
                    type="primary"
                    loading={fieldUpdate === "brandDetectMsg" && loadingField}
                    onClick={() => handleOnUpdateBrandDetect(dataTools.brandDetectMsg, "msg", "brandDetectMsg")}
                  >
                    Save
                  </Button>
                </Input.Group>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>Brand Detect DB Last Update</Typography.Title>
                </Divider>
                <Input.Group compact>
                  <Input
                    value={dataTools.brandDetectMsgLatest}
                    disabled={fieldUpdate === "brandDetectMsgLatest" && loadingField}
                    onChange={(e) => setDataTools((prev: any) => ({ ...prev, brandDetectMsgLatest: e.target.value }))}
                    style={{ width: 'calc(100% - 100px)' }}
                  />
                  <Button
                    type="primary"
                    loading={fieldUpdate === "brandDetectMsgLatest" && loadingField}
                    onClick={() => handleOnUpdateBrandDetect(dataTools.brandDetectMsgLatest, "date", "brandDetectMsgLatest")}
                  >
                    Save
                  </Button>
                </Input.Group>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>AmazonBSR Last Update</Typography.Title>
                </Divider>
                <Space>
                  <i>Updated once a week in random time</i>
                </Space>
                <Space>
                  <label><strong>Last Update:</strong></label> {dataTools.amazonBSRLatest}
                </Space>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>Sale Estimator Data Last Update</Typography.Title>
                </Divider>
                <Space direction="vertical">
                  <i>Updated in two days with each source</i>
                  {dataTools.saleEstDataLatest.length > 0 && dataTools.saleEstDataLatest.map((record: any) => (
                    <>
                      <Typography.Text strong>{record.source}: </Typography.Text>
                      <span>{record.lastUpdate}</span>
                    </>
                  ))}
                </Space>
              </Space>
            </Space>
          )
        }
      />
    </>
  )
}

export default Tools;